import * as React from 'react'
import { isYoutubeUrl } from '@/app/lib/media/youtube'

/** Assumed video length when duration cannot be read (YouTube / CORS / missing URL). */
export const FALLBACK_VIDEO_MINUTES = 8

/** Extra time budgeted per quiz question. */
export const MINUTES_PER_QUESTION = 1

const durationCache = new Map<string, number | null>()
const inflight = new Map<string, Promise<number | null>>()

/**
 * Probe a direct video file URL for duration (seconds).
 * Returns null for YouTube, empty URLs, or probe failures (CORS / unsupported).
 */
export function probeVideoDurationSeconds(videoUrl: string | null | undefined): Promise<number | null> {
  if (!videoUrl || isYoutubeUrl(videoUrl)) return Promise.resolve(null)
  if (durationCache.has(videoUrl)) return Promise.resolve(durationCache.get(videoUrl) ?? null)

  const existing = inflight.get(videoUrl)
  if (existing) return existing

  const promise = new Promise<number | null>((resolve) => {
    const video = document.createElement('video')
    video.preload = 'metadata'
    video.muted = true
    video.playsInline = true

    const finish = (seconds: number | null) => {
      durationCache.set(videoUrl, seconds)
      inflight.delete(videoUrl)
      video.removeAttribute('src')
      video.load()
      resolve(seconds)
    }

    video.onloadedmetadata = () => {
      const d = video.duration
      finish(Number.isFinite(d) && d > 0 ? d : null)
    }
    video.onerror = () => finish(null)

    // Some CDNs require crossOrigin for metadata; if it fails, onerror falls back.
    try {
      video.crossOrigin = 'anonymous'
    } catch {
      // ignore
    }
    video.src = videoUrl
  })

  inflight.set(videoUrl, promise)
  return promise
}

export function estimateLessonMinutes(input: {
  videoDurationSeconds?: number | null
  questionCount?: number | null
  /** Override when video duration is unknown. */
  fallbackVideoMinutes?: number
}): number {
  const fallback = input.fallbackVideoMinutes ?? FALLBACK_VIDEO_MINUTES
  const videoMinutes =
    input.videoDurationSeconds != null && input.videoDurationSeconds > 0
      ? Math.max(1, Math.ceil(input.videoDurationSeconds / 60))
      : fallback
  const quizMinutes = Math.max(0, input.questionCount ?? 0) * MINUTES_PER_QUESTION
  return videoMinutes + quizMinutes
}

/** Format a minute total for the course header (minutes under 1h, else hours + optional remainder). */
export function formatCourseDurationParts(totalMinutes: number): {
  value: number
  remainderMinutes?: number
  unit: 'minutes' | 'hours'
} {
  const mins = Math.max(0, Math.round(totalMinutes))
  if (mins < 60) {
    return { value: mins, unit: 'minutes' }
  }
  const hours = Math.floor(mins / 60)
  const remainder = mins % 60
  return remainder > 0
    ? { value: hours, remainderMinutes: remainder, unit: 'hours' }
    : { value: hours, unit: 'hours' }
}

/**
 * Probe durations for a list of video URLs and return a map of url → seconds
 * (null when unknown). Re-runs when the URL set changes.
 */
export function useVideoDurationSecondsMap(videoUrls: Array<string | null | undefined>) {
  const urlsKey = videoUrls.filter(Boolean).join('\0')
  const urls = React.useMemo(
    () => [...new Set(urlsKey.split('\0').filter(Boolean))],
    [urlsKey],
  )
  const [map, setMap] = React.useState<Record<string, number | null>>({})

  React.useEffect(() => {
    let cancelled = false
    if (urls.length === 0) {
      setMap({})
      return
    }

    Promise.all(urls.map(async (url) => [url, await probeVideoDurationSeconds(url)] as const)).then(
      (entries) => {
        if (cancelled) return
        const next: Record<string, number | null> = {}
        for (const [url, seconds] of entries) next[url] = seconds
        setMap(next)
      },
    )

    return () => {
      cancelled = true
    }
  }, [urls])

  return map
}
